import React, { useEffect, useState } from 'react'

import IntroBannerWithBackground from '../../components/banners/intro-banner-with-background/IntroBannerWithBackground'
import ContentWithStickyColumn from '../../components/content-with-sticky-column/ContentWithStickyColumn'
import DownloadBambooApp from '../../components/download-bamboo-app/DownloadBambooApp'
import FaqAnswerList from '../../components/faq-answer-list/FaqAnswerList'
import FaqSectionList from '../../components/faq-section-list/FaqSectionList'
import SEOControl from '../../components/seoControl/seoControl'
import Spiner from '../../components/ui/spiner/Spiner'
import { colors } from '../../constants/style'
import useGetData from '../../helpers/hooks/useGetData'
// eslint-disable-next-line import/order
import * as styles from './faq.module.scss'

const TITLE = 'Bamboo - Frequently Asked Questions (FAQ)'
const DESCRIPTION =
  'The Bamboo FAQ page is home to all your questions and queries regarding all things Bamboo.'
const KEYWORDS =
  'bamboo, blockchain, gold, silver, precious metals, bitcoin, ethereum, digital assets, litecoin, crypto, buy, invest, invest today, micro investment, microinvestment, micro-investment, solvency protocol, proof of solvency, spare change, budget'

const schemaMarkup = `{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "Where can I download the Bamboo App?",
            "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can download the IOs app from the Apple App Store.<a href=https://apps.apple.com/app/bamboo-effortless-investment/id1386681221>Here's the link</a>. \n You can download the Android App from the Google Play Store. <a href=https://play.google.com/store/apps/details?id=ch.bamboocore.app&amp;hl=en>Here's the link.</a>"}
          }]
        }`

const HELP_CENTER_SIGN_UP_SECTION_ID = '3604296'
const HELP_CENTER_SMSF_SECTION_ID = '3602913'
const HELP_CENTER_SIGN_UP_SECTION_NAME = 'Sign-Up'
const HELP_CENTER_SMSF_SECTION_NAME = 'SMSF'

function FaqPage({ location, pageContext }) {
  // Parse URL search params to get section ID or title
  const getInitialSectionFromParams = () => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      const sectionId = searchParams.get('section')
      return (
        sectionId ||
        (location.hash === '#smsf'
          ? HELP_CENTER_SMSF_SECTION_ID
          : HELP_CENTER_SIGN_UP_SECTION_ID)
      )
    }
    return location.hash === '#smsf'
      ? HELP_CENTER_SMSF_SECTION_ID
      : HELP_CENTER_SIGN_UP_SECTION_ID
  }

  const [activeSectionState, setActiveSectionState] = useState(
    getInitialSectionFromParams()
  )
  const [activeSectionNameState, setActiveSectionNameState] = useState(
    location.hash === '#smsf'
      ? HELP_CENTER_SMSF_SECTION_NAME
      : HELP_CENTER_SIGN_UP_SECTION_NAME
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  const { data: collections, isLoading: isCollectionsLoading } = useGetData(
    `${location.origin}/api/help-center`
  )

  // Update section name when collections are loaded
  useEffect(() => {
    if (collections) {
      const sectionFromParams = getInitialSectionFromParams()
      const section = collections.find(({ id }) => id === sectionFromParams)

      if (section) {
        setActiveSectionState(section.id)
        setActiveSectionNameState(section.title)
      }
    }
  }, [collections])

  // Update URL when section changes
  const updateUrlWithSection = sectionId => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location)
      url.searchParams.set('section', sectionId)
      window.history.pushState({}, '', url)
    }
  }

  const handleCategoryClick = sectionId => {
    setActiveSectionState(sectionId)
    setSearchTerm('')
    setIsSearching(false)
    setSearchResults([])
    updateUrlWithSection(sectionId)

    const sectionName = collections?.find(({ id }) => id === sectionId)?.title

    if (sectionName) setActiveSectionNameState(sectionName)
  }

  const handleSearch = event => {
    const { value } = event.target
    setSearchTerm(value)

    if (!value.trim()) {
      setIsSearching(false)
      setSearchResults([])
      return
    }

    setIsSearching(true)

    // Search across all collections and their articles
    if (collections) {
      const results = []

      collections.forEach(collection => {
        const matchingArticles = collection.articles.filter(article => {
          const searchLower = value.toLowerCase()
          const titleMatch = article.title?.toLowerCase().includes(searchLower)
          const contentMatch = article.body?.toLowerCase().includes(searchLower)

          return titleMatch || contentMatch
        })

        if (matchingArticles.length > 0) {
          results.push({
            sectionId: collection.id,
            sectionName: collection.title,
            articles: matchingArticles,
          })
        }
      })

      setSearchResults(results)
    }
  }

  const collectionNamesList = collections?.map(({ id, title }) => {
    return { id, title }
  })

  // Determine what sections to show in the sidebar
  const getSections = () => {
    if (isSearching) {
      return searchResults.map(result => ({
        id: result.sectionId,
        title: result.sectionName,
      }))
    }

    if (pageContext?.isUSRegion) {
      return collectionNamesList?.filter(
        ({ id }) => id !== HELP_CENTER_SMSF_SECTION_ID
      )
    }

    return collectionNamesList
  }

  const stickySection = (
    <div className={styles.categories}>
      {collections && (
        <FaqSectionList
          onCategoryClick={handleCategoryClick}
          sections={getSections()}
          activeSectionId={
            isSearching ? searchResults[0]?.sectionId || '' : activeSectionState
          }
          label={isSearching ? 'Search results' : 'Change category'}
        />
      )}
    </div>
  )

  // Render search results or regular FAQ content
  const renderContent = () => {
    if (!collections) {
      return (
        <p className={styles.text}>
          Sorry, we couldn't load the FAQ information.
        </p>
      )
    }

    if (isSearching) {
      if (searchResults.length > 0) {
        return (
          <div className={styles.searchResults}>
            {searchResults.map((result, idx) => (
              <div key={idx} className={styles.searchResultSection}>
                <FaqAnswerList
                  className={styles.answerGroup}
                  questions={result.articles}
                  sectionId={result.sectionId}
                  name={result.sectionName}
                />
              </div>
            ))}
          </div>
        )
      }

      return (
        <div className={styles.noResults}>
          <p>No matching FAQs found.</p>
        </div>
      )
    }

    return (
      <FaqAnswerList
        className={styles.answerGroup}
        questions={
          collections?.find(({ id }) => id === activeSectionState)?.articles
        }
        sectionId={activeSectionState}
        name={activeSectionNameState}
      />
    )
  }

  return (
    <>
      <SEOControl
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        schemaMarkup={schemaMarkup}
      />
      <IntroBannerWithBackground
        title="Frequently asked questions"
        backgroundColor={colors.blue400}
        search
        handleSearch={handleSearch}
        searchValue={searchTerm}
      />

      {isCollectionsLoading ? (
        <div className={styles.container}>
          <Spiner reversed width="50px" height="50px" />
        </div>
      ) : (
        <ContentWithStickyColumn
          info={stickySection}
          id={`${location?.hash?.slice(1)}`}
        >
          {renderContent()}
        </ContentWithStickyColumn>
      )}
      <DownloadBambooApp />
    </>
  )
}

export default FaqPage
